// Formularios de alta y edicion de la nomina. La validacion de forma la hace
// la logica (logic/fleet.ts) y el parser de contracts en el cliente; lo que
// decide si se puede (rol, titular, patente repetida) lo decide la API y aca
// solo se traduce su respuesta.
//
// En la edicion el codigo de movil se cambia (contratos 0.6.7; la unicidad en
// la nomina la decide la API con 409) y el titular se muestra sin poder
// cambiarse: el contrato de edicion no lo acepta.
//
// PII: el telefono y el documento del conductor aparecen solo aca, nunca en
// la lista.
import { useState } from "react";
import type { Driver, RosterHolder, SiteVehicle, UpdateDriverRequest, UpdateVehicleRequest, VehicleType } from "@ucorp/contracts";
import { Alert } from "../components/ui/alert.js";
import { Button } from "../components/ui/button.js";
import {
  EMPTY_DRIVER_DRAFT,
  EMPTY_VEHICLE_DRAFT,
  VEHICLE_TYPES,
  VEHICLE_TYPE_LABEL,
  driverCreateRequest,
  driverDraftFrom,
  driverUpdateRequest,
  isEmptyUpdate,
  vehicleCreateRequest,
  vehicleDraftFrom,
  vehicleUpdateRequest,
  type DriverDraft,
  type VehicleDraft,
} from "../logic/fleet.js";
import { Field, HolderSelect, SelectField, errorCopy, type HoldersState } from "./parts.js";
import type { DriverDraftRequest, VehicleDraftRequest } from "./port.js";

const LOCKED_HINT = "Se fija en el alta y no se cambia desde acá.";
const CODE_EDIT_HINT = "Único en la nómina de la empresa; vale en todas sus plantas.";

function holderText(holder: RosterHolder | undefined): string {
  if (holder === undefined) return "Sin dato";
  return holder.relation === "contractor" ? `${holder.brandName} (empresa madre)` : `${holder.brandName} (tercerizada)`;
}

/** Codigo editable y titular fijo, en la edicion. */
function EditIdentity({
  code,
  holder,
  onCode,
}: {
  code: string;
  holder: RosterHolder | undefined;
  onCode: (code: string) => void;
}): React.JSX.Element {
  return (
    <>
      <Field label="Código de móvil" hint={CODE_EDIT_HINT} required value={code} onChange={(e) => onCode(e.target.value)} />
      <Field label="Titular" hint={LOCKED_HINT} value={holderText(holder)} disabled readOnly />
    </>
  );
}

interface FormShellProps {
  title: string;
  error: string | undefined;
  busy: boolean;
  canSubmit: boolean;
  submitLabel: string;
  blockedReason: string | undefined;
  onSubmit: () => void;
  onCancel: () => void;
  children: React.ReactNode;
}

function FormShell({
  title,
  error,
  busy,
  canSubmit,
  submitLabel,
  blockedReason,
  onSubmit,
  onCancel,
  children,
}: FormShellProps): React.JSX.Element {
  // El titulo lo muestra la hoja lateral que lo contiene; aca queda como nombre accesible del formulario.
  return (
    <form
      aria-label={title}
      className="flex flex-col gap-4"
      onSubmit={(event) => {
        event.preventDefault();
        if (canSubmit && !busy) onSubmit();
      }}
    >
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">{children}</div>
      {blockedReason !== undefined && <p className="m-0 text-xs text-texto-suave">{blockedReason}</p>}
      {error !== undefined && <Alert variant="error">{error}</Alert>}
      <div className="flex flex-wrap gap-2 border-t border-divisor pt-4">
        <Button type="submit" disabled={!canSubmit || busy}>
          {busy ? "Guardando..." : submitLabel}
        </Button>
        <Button variant="secundario" disabled={busy} onClick={onCancel}>
          Cancelar
        </Button>
      </div>
    </form>
  );
}

/** Estado comun de un formulario: borrador, envio en curso y error traducido. */
function useSubmit<T>(onSaved: (row: T) => void) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | undefined>(undefined);
  function run(action: () => Promise<T>): void {
    setBusy(true);
    setError(undefined);
    action()
      .then((row) => {
        setBusy(false);
        onSaved(row);
      })
      .catch((failure: unknown) => {
        setBusy(false);
        setError(errorCopy(failure, "save"));
      });
  }
  return { busy, error, run };
}

// --- Vehiculos ---------------------------------------------------------------

interface VehicleFormProps {
  /** Ausente: alta. */
  current?: SiteVehicle;
  holders: HoldersState;
  create: (body: VehicleDraftRequest) => Promise<SiteVehicle>;
  update: (vehicleId: string, body: UpdateVehicleRequest) => Promise<SiteVehicle>;
  onSaved: (vehicle: SiteVehicle) => void;
  onCancel: () => void;
}

export function VehicleForm({ current, holders, create, update, onSaved, onCancel }: VehicleFormProps): React.JSX.Element {
  const [draft, setDraft] = useState<VehicleDraft>(() => (current === undefined ? EMPTY_VEHICLE_DRAFT : vehicleDraftFrom(current)));
  const { busy, error, run } = useSubmit(onSaved);
  const set = (patch: Partial<VehicleDraft>) => setDraft((d) => ({ ...d, ...patch }));

  const createBody = current === undefined ? vehicleCreateRequest(draft) : null;
  const updateBody = current === undefined ? null : vehicleUpdateRequest(draft, current);
  const invalid = current === undefined ? createBody === null : updateBody === null;
  const unchanged = updateBody !== null && isEmptyUpdate(updateBody);
  const blockedReason = invalid
    ? current === undefined
      ? "Completar patente y capacidad (y el año, si se carga, con números)."
      : "Completar código de móvil, patente y capacidad (y el año, si se carga, con números)."
    : unchanged
      ? "No hay cambios para guardar."
      : undefined;

  function submit(): void {
    if (current === undefined) {
      if (createBody !== null) run(() => create(createBody));
    } else if (updateBody !== null) {
      run(() => update(current.id, updateBody));
    }
  }

  return (
    <FormShell
      title={current === undefined ? "Alta de vehículo" : `Editar vehículo ${current.code}`}
      error={error}
      busy={busy}
      canSubmit={!invalid && !unchanged}
      submitLabel={current === undefined ? "Dar de alta" : "Guardar cambios"}
      blockedReason={blockedReason}
      onSubmit={submit}
      onCancel={onCancel}
    >
      {current === undefined ? (
        <>
          <Field
            label="Código de móvil"
            hint="Opcional. Si queda vacío, se usa el identificador que asigna la planta."
            value={draft.code}
            onChange={(e) => set({ code: e.target.value })}
          />
          <HolderSelect holders={holders} value={draft.holderId} onChange={(holderId) => set({ holderId })} />
        </>
      ) : (
        <EditIdentity code={draft.code} holder={current.holder} onCode={(code) => set({ code })} />
      )}
      <SelectField label="Tipo" value={draft.type} onChange={(e) => set({ type: e.target.value as VehicleType })}>
        {VEHICLE_TYPES.map((type) => (
          <option key={type} value={type}>
            {VEHICLE_TYPE_LABEL[type]}
          </option>
        ))}
      </SelectField>
      <Field label="Patente" required value={draft.plate} onChange={(e) => set({ plate: e.target.value })} autoComplete="off" />
      <Field
        label="Capacidad"
        hint="Pasajeros sentados, sin contar al conductor."
        required
        inputMode="numeric"
        value={draft.capacity}
        onChange={(e) => set({ capacity: e.target.value })}
      />
      <Field
        label="Año del modelo"
        hint="Opcional. Algunas plantas ponen una antigüedad máxima."
        inputMode="numeric"
        value={draft.year}
        onChange={(e) => set({ year: e.target.value })}
      />
    </FormShell>
  );
}

// --- Conductores -------------------------------------------------------------

interface DriverFormProps {
  current?: Driver;
  holders: HoldersState;
  create: (body: DriverDraftRequest) => Promise<Driver>;
  update: (driverId: string, body: UpdateDriverRequest) => Promise<Driver>;
  onSaved: (driver: Driver) => void;
  onCancel: () => void;
}

export function DriverForm({ current, holders, create, update, onSaved, onCancel }: DriverFormProps): React.JSX.Element {
  const [draft, setDraft] = useState<DriverDraft>(() => (current === undefined ? EMPTY_DRIVER_DRAFT : driverDraftFrom(current)));
  const { busy, error, run } = useSubmit(onSaved);
  const set = (patch: Partial<DriverDraft>) => setDraft((d) => ({ ...d, ...patch }));

  const createBody = current === undefined ? driverCreateRequest(draft) : null;
  const updateBody = current === undefined ? null : driverUpdateRequest(draft, current);
  const invalid = current === undefined ? createBody === null : updateBody === null;
  const unchanged = updateBody !== null && isEmptyUpdate(updateBody);
  const blockedReason = invalid
    ? current === undefined ? "Completar nombre y apellido." : "Completar código de móvil y nombre y apellido."
    : unchanged ? "No hay cambios para guardar." : undefined;

  function submit(): void {
    if (current === undefined) {
      if (createBody !== null) run(() => create(createBody));
    } else if (updateBody !== null) {
      run(() => update(current.id, updateBody));
    }
  }

  return (
    <FormShell
      title={current === undefined ? "Alta de conductor" : `Editar conductor ${current.code}`}
      error={error}
      busy={busy}
      canSubmit={!invalid && !unchanged}
      submitLabel={current === undefined ? "Dar de alta" : "Guardar cambios"}
      blockedReason={blockedReason}
      onSubmit={submit}
      onCancel={onCancel}
    >
      {current === undefined ? (
        <>
          <Field
            label="Código de móvil"
            hint="Opcional. Si queda vacío, se usa el identificador que asigna la planta."
            value={draft.code}
            onChange={(e) => set({ code: e.target.value })}
          />
          <HolderSelect holders={holders} value={draft.holderId} onChange={(holderId) => set({ holderId })} />
        </>
      ) : (
        <EditIdentity code={draft.code} holder={current.holder} onCode={(code) => set({ code })} />
      )}
      <Field
        label="Nombre y apellido"
        required
        value={draft.name}
        onChange={(e) => set({ name: e.target.value })}
        autoComplete="off"
      />
      <Field
        label="Documento"
        hint="Opcional. DNI u otro; no se muestra en la lista."
        value={draft.idDocument}
        onChange={(e) => set({ idDocument: e.target.value })}
        autoComplete="off"
      />
      <Field
        label="Teléfono"
        hint="Opcional. No se muestra en la lista."
        type="tel"
        value={draft.phone}
        onChange={(e) => set({ phone: e.target.value })}
        autoComplete="off"
      />
    </FormShell>
  );
}
